import {Cloudinary} from '@cloudinary/url-gen';
import {defaultImage, format, quality} from '@cloudinary/url-gen/actions/delivery';
import {name} from '@cloudinary/url-gen/actions/namedTransformation';
import {Resize} from '@cloudinary/url-gen/actions/resize';
import {AutoFocus} from '@cloudinary/url-gen/qualifiers/autoFocus';
import {FocusOn} from '@cloudinary/url-gen/qualifiers/focusOn';
import {Format} from '@cloudinary/url-gen/qualifiers/format';
import {autoGravity} from '@cloudinary/url-gen/qualifiers/gravity';
import {Quality} from '@cloudinary/url-gen/qualifiers/quality';

import {CLOUDINARY_CATALOG_NAME, CLOUDINARY_DEFAULT_LOGO_NAME, CLOUDINARY_PRODUCT_NAME, CLOUDINARY_URL} from './consts';

/**
 * Format of the Cloudinary URL is https://media.stubcloudstatic.com/<transformations>/<public_id>.<extension>
 * TODO Move to Env File when we have multiple url for the QA and Prod Env
 *
 */

/**
 * Currently we are using 2 Cloudinary accounts - StubHub-Product and StubHub-Catalog.
 * Imagery/logos for Partner teams and homepage jumbotron are currently pulled from StubHub-Product.
 * BUT partner team imagery/logos WILL EVENTUALLY NEED to be pulled from StubHub-Catalog,
 * because the StubHub-product/partner folder will be deprecated.
 * The new StubHub-Catalog/SH-Partner folder should hold all of the logos/imagery for partners, but at the moment it is not complete.
 * Everything else imagery-wise for top of the funnel pages will now be pulled from StubHub-Catalog.
 */

const cloudinaryProductBaseURL = '//media.stubcloudstatic.com/stubhub-product';
const cloudinaryCatalogBaseURL = '//media.stubcloudstatic.com/stubhub-catalog';

const supportedImageTypesRegExp = new RegExp(/(jpe?g|svg|png|gif|webp|bmp)$/i);
const supportedVideoTypesRegExp = new RegExp(/(mp4|ogg|webm|mpe?g)$/i);

const CloudinaryCatalog = new Cloudinary({
  cloud: {
    cloudName: CLOUDINARY_CATALOG_NAME,
  },
  url: {
    secureDistribution: CLOUDINARY_URL,
    secure: true,
  },
});
const CloudinaryProduct = new Cloudinary({
  cloud: {
    cloudName: CLOUDINARY_PRODUCT_NAME,
  },
  url: {
    secureDistribution: CLOUDINARY_URL,
    secure: true,
  },
});

/**
 * Method to apply Cloudinary media transformation for StubHub-Product account
 * @param transformations [object] The Object containing the transformations to be applied to the Cloudinary media resource.
 * @return string The Transformation to be used for the media resource request.
 * @TODO handler unused transformations like `aspect_ratio = ar`
 *
 */
const applyMediaTransformations = (transformations) => {
  const transforms = Object.keys(transformations);

  return transforms.reduce((mediaURLTransforms, transform) => {
    const transformValue = transformations[transform];
    if (transformValue || transformValue === 0) {
      mediaURLTransforms = `${mediaURLTransforms}${mediaURLTransforms.length ? ',' : ''}${transform.charAt(
        0
      )}_${transformValue}`;
    }

    return mediaURLTransforms;
  }, '');
};

/**
 * Method to apply Cloudinary media dimensions (height, width) for StubHub-Catalog account
 */
const applyMediaDimensions = (width, height) => {
  width = width ? `,$w_${width}_mul_3` : '';
  height = height ? `,$h_${height}_mul_3` : '';

  return width || height ? `${width}${height}` : '';
};

/**
 * Method to concatenate the Cloudinary image URL for StubHub-Product.
 * This method is currently used to grab partner logos and jumbotron imagery, using the given source to construct the image URL.
 * @param src [string] The source URL for the image.
 * @param publicId [string] The ID reference for the image in Cloudinary.
 * @param extension [string] The extension to be used for fetching the image.
 * @param transformations [object] The object containing the transformations to be applied to the image.
 * @param defaultImg [string] The string file name that the imagery should default to as a fallback.
 * @param useNamedTransforms [bool] Determines whether a standard set of facial detection functions is
 *  used to reposition the focus area of the image
 *
 */

const getProductImage = ({src, publicId, extension, defaultImg, useNamedTransforms, ...transformations}) => {
  if (src || (publicId && extension)) {
    // Default image is either: generic blank img, generic with logo, or transparent
    const defaultTransformation = defaultImg ? defaultImg : 'defaultLogo.jpg';
    // Transformations that should be applied regardless of use case when using Cloudinary
    const generalTransforms = 'q_auto:low,f_auto,c_fill';
    // Transformations passed by the component consuming Cloudinary, most often width & height
    const specificTransforms = applyMediaTransformations(transformations);
    const imageTransformations = generalTransforms + (specificTransforms !== '' ? `,${specificTransforms}` : '');

    const imageWithExtension = publicId && extension && `${publicId}.${extension}`;
    const imagePath = `${src || ''}${imageWithExtension ? `/${imageWithExtension}` : ''}`;
    // For most dynamic imagery from catalog, we want to use this transform to center the frame
    const namedTransformation = useNamedTransforms ? 't_FACE-FACES-NOFACE/' : '';

    return `${cloudinaryProductBaseURL}${
      imageTransformations ? `/d_${defaultTransformation}/${namedTransformation}${imageTransformations}` : ''
    }${imagePath.charAt(0) === '/' ? imagePath : `/${imagePath}`}`;
  }
};

/**
 * Method to concatenate the Cloudinary image URL for StubHub-Catalog.
 * This method will construct the image URL using the cloudinaryPublicId provided (publicIdSrc).
 * @param publicIdSrc [string] The Cloudinary Public ID required to construct an img URL.
 * @param defaultImg [string] The string file name that the imagery should default to as a fallback.
 * @param useNamedTransforms [bool] Determines whether a standard set of facial detection functions is
 *  used to reposition the focus area of the image.
 * @param height [number] The set height for the image. Required if using namedTransform.
 * @param width [number] The set width for the image. Required if using namedTransform.
 */

const getCatalogImage = ({publicIdSrc, defaultImg, useNamedTransforms, height, width, useGravityTransforms}) => {
  // Default image is either: generic blank img, generic with logo, or transparent
  const defaultTransforms = defaultImg ? defaultImg : 'defaultLogo.jpg';
  // Required transformations for Catalog imagery
  const generalTransforms = `q_auto:low,f_auto,c_fill${useGravityTransforms ? ',g_auto' : ''}`;
  // Dimension transformations - width + height
  const dimensionTransforms = applyMediaDimensions(width, height);

  const imageTransforms = generalTransforms + dimensionTransforms;
  const namedTransforms = useNamedTransforms ? 't_f-fs-0fv,' : '';
  const allTransforms = imageTransforms ? `/d_${defaultTransforms}/${namedTransforms}${imageTransforms}` : '';

  const revisedSrc = publicIdSrc.charAt(0) === '/' ? publicIdSrc.slice(1) : publicIdSrc;
  const imagePath = `${cloudinaryCatalogBaseURL}${allTransforms}/${revisedSrc}`;

  return imagePath;
};

/**
 * Method to determine whether we need imagery from the StubHub-Catalog or StubHub-Product account.
 * See top of the page for imagery account split.
 *
 * @deprecated Please use `getCloudinaryImage` instead. This will be removed in future release
 */
const getCloudinaryImageURL = ({
  src = '',
  publicIdSrc = '',
  publicId,
  extension,
  defaultImg,
  useNamedTransforms = true,
  ...transformations
}) => {
  if (!publicIdSrc) {
    return getProductImage({src, publicId, extension, defaultImg, useNamedTransforms, ...transformations});
  }

  return getCatalogImage({publicIdSrc, defaultImg, useNamedTransforms, ...transformations});
};

/**
 * Method to concatenate the Cloudinary video URL.
 * @param src [string] The source URL for the video.
 * @param publicId [string] The ID reference for the video in Cloudinary.
 * @param extension [string] The extension to be used for fetching the video.
 * @param transformations [object] The object containing the transformations to be applied to the video.
 *
 */
const getCloudinaryVideoURL = ({src, publicId, extension, ...transformations}) => {
  const BASE_URL = `${cloudinaryProductBaseURL}/video/upload`;
  if (src || (publicId && extension)) {
    // Transformations that should be applied regardless of use case when using Cloudinary
    const generalTransforms = 'ac_none';
    // Transformations passed by the component consuming Cloudinary, most often width & height
    const specificTransforms = applyMediaTransformations(transformations);
    const videoTransformations = generalTransforms + (specificTransforms !== '' ? `,${specificTransforms}` : '');
    const videoWithExtension = publicId && extension && `${publicId}.${extension}`;
    const videoPath = `${src || ''}${videoWithExtension ? `/${videoWithExtension}` : ''}`;

    return `${BASE_URL}/${videoTransformations}${videoPath}`;
  }
};

/**
 * Checks if the given action is a function or a valid value from the enum.
 *
 * @returns the action/qualifier function if valid, null otherwise
 */
const getValidActionOrQualifier = (value, validValues = {}) => {
  if (typeof value === 'function') {
    return value;
  }
  if (typeof value === 'string' && Object.keys(validValues).includes(value)) {
    return validValues[value];
  }

  return null;
};

/**
 * Returns a CloudinaryImage object preconfigured with some values
 * The source is determined by `REACT_APP_CLOUDINARY_URL` and `REACT_APP_CLOUDINARY_CATALOG_NAME`, that will default to `media.stubcloudstatic.com` and `stubhub-catalog`
 *
 * Defaults (if nothing is passed) are: resizing as `fill`, with quality 'auto:low' and format `f_auto`.
 * Also there's a default image set to CLOUDINARY_DEFAULT_LOGO_NAME || 'defaultLogo.jpg' that can be overriden with the `defaultImg` param
 *
 * For `resizeType`, `focusOn`, `quality` and `format` you can pass a valid string value or the function from the Cloudinary corresponding enum
 *
 * @example
 *  helper
 *    .getCloudinaryImage({
 *      publicIdSrc: performerImageSrc,
 *      width: 800,
 *      aspectRatio: 1.73,
 *      focusOnValue: 'faces',
 *      qualityValue: 'auto:low',
 *      resizeType: 'limitFill',
 *    }).toURL()
 *
 * @example
 *  helper
 *    .getCloudinaryImage({
 *      publicIdSrc: performerImageSrc,
 *      width: 800,
 *      aspectRatio: 1.73,
 *      focusOnValue: FocusOn.faces,
 *      qualityValue: Quality.autoLow,
 *      resizeType: Resize.limitFill,
 *    }).toURL()
 *
 * @returns CloudinaryImage object
 */
const getCloudinaryImage = ({
  src,
  publicIdSrc,
  defaultImg,
  resizeType = Resize.fill,
  height,
  width,
  aspectRatio,
  focusOn: focusOnValue,
  quality: qualityValue,
  format: formatValue = Format.auto,
  namedTransformation,
}) => {
  if (!publicIdSrc && !src) {
    return null;
  }

  const cloudinarySource = publicIdSrc ? CloudinaryCatalog : CloudinaryProduct;
  const performerImage = cloudinarySource.image(publicIdSrc || src);

  if (namedTransformation) {
    const formatQualifier = getValidActionOrQualifier(formatValue, Format) || Format.auto;
    const qualityQualifier = getValidActionOrQualifier(qualityValue, Quality);
    performerImage.namedTransformation(name(namedTransformation)).delivery(format(formatQualifier()));

    if (qualityQualifier) {
      performerImage.delivery(quality(qualityQualifier()));
    }
  } else {
    const resizeTypeAction = getValidActionOrQualifier(resizeType, Resize) || Resize.fill;
    const resizeOptions = resizeTypeAction();
    if (width && !Number.isNaN(Number(width))) {
      resizeOptions.width(width);
    }
    if (height && !Number.isNaN(Number(height))) {
      resizeOptions.height(height);
    }
    if (aspectRatio) {
      resizeOptions.aspectRatio(typeof aspectRatio === 'function' ? aspectRatio() : aspectRatio);
    }
    if (focusOnValue) {
      const focusOnQualifier = getValidActionOrQualifier(focusOnValue, FocusOn);
      if (focusOnQualifier) {
        resizeOptions.gravity(autoGravity().autoFocus(AutoFocus.focusOn(focusOnQualifier())));
      }
    }

    const formatQualifier = getValidActionOrQualifier(formatValue, Format) || Format.auto;
    const qualityQualifier = getValidActionOrQualifier(qualityValue, Quality) || Quality.autoLow;

    performerImage
      .resize(resizeOptions)
      .delivery(format(formatQualifier()))
      .delivery(defaultImage(defaultImg || CLOUDINARY_DEFAULT_LOGO_NAME))
      .delivery(quality(qualityQualifier()));
  }

  return performerImage;
};

export default {
  getCloudinaryImage,
  getCloudinaryImageURL,
  getCloudinaryVideoURL,
  supportedImageTypesRegExp,
  supportedVideoTypesRegExp,
};
